import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./HelpChat.css";

function HelpChat() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = React.useRef(null);

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");

    // Add user message to chat
    const newMessages = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://192.168.68.67:5000"
        }/api/help/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: userMessage,
            conversationHistory: messages,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setMessages(data.conversationHistory);
      } else {
        console.error("Chat API error:", data);
        // Add error message
        setMessages([
          ...newMessages,
          {
            role: "assistant",
            content: "Sorry, I encountered an error. Please try again.",
          },
        ]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Don't show chat on public pages
  if (!user) {
    return null;
  }

  return (
    <>
      {/* Chat Bubble */}
      {!isOpen && (
        <button className="help-chat-bubble" onClick={() => setIsOpen(true)}>
          <span className="bubble-icon">💬</span>
          <span className="bubble-text">Help</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="help-chat-window">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-content">
              <span className="chat-icon"></span>
              <div>
                <h3>Playmaker Assistant</h3>
                <p>Ask me anything about using Playmaker!</p>
              </div>
            </div>
            <button className="chat-close" onClick={() => setIsOpen(false)}>
              ✕
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="chat-welcome">
                <h4>Hi! I'm here to help!</h4>
                <p>Ask me about:</p>
                <ul>
                  <li>How it compares to other stat tracking tools</li>
                  <li>Tracking stats & analytics</li>
                  <li>BragCards & graphics</li>
                  <li>Subscription plans</li>
                </ul>
              </div>
            )}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`chat-message ${
                  msg.role === "user" ? "user" : "assistant"
                }`}
              >
                <div
                  className="message-content"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="chat-message assistant">
                <div className="message-content typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {(() => {
            const userMessageCount = messages.filter(
              (m) => m.role === "user",
            ).length;
            return userMessageCount >= 5 ? (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <p
                  style={{
                    fontSize: "14px",
                    color: "#718096",
                    marginBottom: "8px",
                  }}
                >
                  Need more help?
                </p>
                <a
                  href="mailto:contact@playmakermediakit.com"
                  style={{
                    color: "#e53e3e",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  contact@playmakermediakit.com
                </a>
              </div>
            ) : (
              <div className="chat-input-container">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask a question..."
                  rows="2"
                  disabled={loading}
                />
                <button
                  className="chat-send-btn"
                  onClick={sendMessage}
                  disabled={loading || !inputMessage.trim()}
                >
                  {loading ? "..." : "→"}
                </button>
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
}

export default HelpChat;
